// Layer 2 (Engine): one pure settlement calculation for Capacity, NSR and
// cost-period projections. Feed mass is supplied on the smelter's metric-tonne
// basis; callers convert short-ton feed once before they arrive here.
import type { MetalPriceState } from '@/versions/v1/schema/capacity';
import { BUNKER_HILL_SMELTER_SCHEDULE, defaultSmelterPriceState } from './bunker-hill-smelter-terms';
import { GRAMS_PER_TROY_OUNCE } from './unit-conversion';

export type SmelterSettlementInput = {
  feedShortTonsPerDay: number;
  feedMetricTonnesPerDay: number;
  headGrades: { leadFraction: number; zincFraction: number; silverOzPerShortTon: number };
  recoveries: { leadPct: number; zincPct: number; silverPct: number };
  concentrateGrades: { leadPct: number; zincPct: number };
  assays: { leadAsPct: number; leadHgPpm: number; zincAsPct: number; zincPbPct: number };
  prices?: MetalPriceState;
};

export type SettlementProduct = {
  concentrateDmtPerDay: number;
  recoveredMetalPerDay: number;
  payableMetalPerDay: number;
  grossRevenueA: number;
  treatmentChargeA: number;
  transportA: number;
  penaltiesA: number;
  netNsrA: number;
};

export type SmelterSettlementRead = {
  lead: SettlementProduct & { arsenicAssayPct: number };
  zinc: SettlementProduct & { arsenicAssayPct: number; leadAssayPct: number };
  silver: SettlementProduct;
  totalNsrA: number;
  treatmentChargesA: number;
  transportA: number;
  penaltiesA: number;
};

export function penaltyCostPerElement(
  product: 'Pb' | 'Zn',
  element: string,
  assay: number,
  concentrateDmt: number,
): number {
  const schedule = BUNKER_HILL_SMELTER_SCHEDULE.products.find((row) => row.product === product)!;
  const term = schedule.penalties.find((candidate) => candidate.element === element);
  if (!term) throw new Error(`No ${element} penalty term for ${product} concentrate`);
  return Math.max(0, assay - term.limit) * term.ratePerUnit * concentrateDmt;
}

function penaltyCost(
  product: 'Pb' | 'Zn',
  concentrateDmt: number,
  assays: Record<string, number>,
): number {
  const schedule = BUNKER_HILL_SMELTER_SCHEDULE.products.find((row) => row.product === product)!;
  return schedule.penalties.reduce((total, term) =>
    total + penaltyCostPerElement(product, term.element, assays[term.element] ?? 0, concentrateDmt), 0);
}

function payablePbAg(
  recoveredSilverOz: number,
  leadConcentrateDmt: number,
): number {
  const terms = BUNKER_HILL_SMELTER_SCHEDULE.silverOnLead;
  const deductionOz = leadConcentrateDmt * terms.flatDeductionGt / GRAMS_PER_TROY_OUNCE;
  return Math.max(0, recoveredSilverOz * terms.payableFraction - deductionOz);
}

/** Price feed metals and apply the schedule's payability, deductions and terms. */
export function smelterSettlement(input: SmelterSettlementInput): SmelterSettlementRead {
  const schedule = BUNKER_HILL_SMELTER_SCHEDULE;
  const pbSchedule = schedule.products.find((row) => row.product === 'Pb')!;
  const znSchedule = schedule.products.find((row) => row.product === 'Zn')!;
  const prices = input.prices ?? defaultSmelterPriceState();
  const recoveredPb = input.feedMetricTonnesPerDay * input.headGrades.leadFraction * input.recoveries.leadPct / 100;
  const recoveredZn = input.feedMetricTonnesPerDay * input.headGrades.zincFraction * input.recoveries.zincPct / 100;
  const recoveredAgOz = input.feedShortTonsPerDay * input.headGrades.silverOzPerShortTon * input.recoveries.silverPct / 100;
  const leadConcentrateDmt = input.concentrateGrades.leadPct > 0
    ? recoveredPb / (input.concentrateGrades.leadPct / 100)
    : 0;
  const zincConcentrateDmt = input.concentrateGrades.zincPct > 0
    ? recoveredZn / (input.concentrateGrades.zincPct / 100)
    : 0;
  const payablePb = recoveredPb * pbSchedule.payable.payableFraction;
  const zincGradePct = input.concentrateGrades.zincPct;
  const minimumDeduction = znSchedule.payable.gradeDeduction ?? 0;
  const zincPayableGrade = Math.max(0, zincGradePct - minimumDeduction);
  const payableZn = zincConcentrateDmt * (zincPayableGrade / 100) * znSchedule.payable.payableFraction;
  const payableAg = payablePbAg(recoveredAgOz, leadConcentrateDmt);
  const pbWmt = leadConcentrateDmt / (1 - pbSchedule.moistureFraction);
  const znWmt = zincConcentrateDmt / (1 - znSchedule.moistureFraction);
  const leadPenalties = penaltyCost('Pb', leadConcentrateDmt, {
    As: input.assays.leadAsPct,
    Hg: input.assays.leadHgPpm,
  });
  const zincPenalties = penaltyCost('Zn', zincConcentrateDmt, {
    As: input.assays.zincAsPct,
    Pb: input.assays.zincPbPct,
  });
  const leadTreatment = leadConcentrateDmt * pbSchedule.treatmentChargePerDmt;
  const zincTreatment = zincConcentrateDmt * znSchedule.treatmentChargePerDmt;
  const leadTransport = pbWmt * pbSchedule.freightPerWmt;
  const zincTransport = znWmt * znSchedule.freightPerWmt;
  const leadRevenue = payablePb * prices.leadPerTonne;
  const zincRevenue = payableZn * prices.zincPerTonne;
  const silverRevenue = payableAg * prices.silverPerOz;

  const lead: SmelterSettlementRead['lead'] = {
    concentrateDmtPerDay: leadConcentrateDmt,
    recoveredMetalPerDay: recoveredPb,
    payableMetalPerDay: payablePb,
    grossRevenueA: leadRevenue,
    treatmentChargeA: leadTreatment,
    transportA: leadTransport,
    penaltiesA: leadPenalties,
    netNsrA: leadRevenue - leadTreatment - leadTransport - leadPenalties,
    arsenicAssayPct: input.assays.leadAsPct,
  };
  const zinc: SmelterSettlementRead['zinc'] = {
    concentrateDmtPerDay: zincConcentrateDmt,
    recoveredMetalPerDay: recoveredZn,
    payableMetalPerDay: payableZn,
    grossRevenueA: zincRevenue,
    treatmentChargeA: zincTreatment,
    transportA: zincTransport,
    penaltiesA: zincPenalties,
    netNsrA: zincRevenue - zincTreatment - zincTransport - zincPenalties,
    arsenicAssayPct: input.assays.zincAsPct,
    leadAssayPct: input.assays.zincPbPct,
  };
  const silver: SettlementProduct = {
    concentrateDmtPerDay: 0,
    recoveredMetalPerDay: recoveredAgOz,
    payableMetalPerDay: payableAg,
    grossRevenueA: silverRevenue,
    treatmentChargeA: 0,
    transportA: 0,
    penaltiesA: 0,
    netNsrA: silverRevenue,
  };
  return {
    lead,
    zinc,
    silver,
    totalNsrA: lead.netNsrA + zinc.netNsrA + silver.netNsrA,
    treatmentChargesA: leadTreatment + zincTreatment,
    transportA: leadTransport + zincTransport,
    penaltiesA: leadPenalties + zincPenalties,
  };
}
